import API from "api/API"
import {ApiDeleteResult, ApiPatchResult, ApiPutResult} from "app/Interface/Api/ApiResult"
import {
  ApiSystemLayout,
  ApiSystemLayoutKeys,
  ApiSystemLayoutPatch,
  ApiSystemLayoutPut,
  ApiSystemLayoutResult,
  ApiSystemLayoutTypes
} from "app/Interface/Api/System/ApiSystemLayout"
import {Module} from "const/Module"
import Logger from "logger/logger"
import {useEffect, useState} from "react"
import {useTranslation} from "react-i18next"
import {toast} from "react-toastify"
import {systemLayoutQuery, systemLayoutStore} from "store/system/layout/system.layout.store"

function ApiSystemLayoutService(caller: string) {
  const {t} = useTranslation('System.SystemLayout')

  const url = `/system/${Module.layout}`

  const [layoutState, setLayoutState] = useState<ApiSystemLayout>()

  useEffect(() => {
    Logger('Service.API.SystemLayout.init', caller)
    return () => {
      Logger('Service.API.SystemLayout.unloading', caller)
    }
  }, [])

  function processData(method: 'put' | 'patch'): ApiSystemLayout | undefined {
    let data: Partial<ApiSystemLayoutPut | ApiSystemLayoutPatch> | undefined = systemLayoutStore.getValue()[method]

    if (!data || !data.moduleId || !data.crudOperator)
      throw Error("Missing moduleId or crudOperator")

    if (!data.name) {
      toast.warning(t('api.name.notNull'))
      return
    }

    if (!data.tabs) {
      toast.warning(t('api.tabs.notNull'))
      return
    }

    if (method === "patch" && !data.id) {
      toast.warning(t('api.id.notNull'))
      return
    }

    let result: ApiSystemLayoutPut = {
      name: data.name,
      moduleId: data.moduleId,
      crudOperator: data.crudOperator,
      tabs: data.tabs,
      private: data.private
    }

    if (method === "patch")
      result.id = data.id

    return result
  }

  const clearStoreState = () => {
    systemLayoutStore.update(state => {
      state.put = undefined
      state.patch = undefined
    })
  }

  const putLayout: ApiPutResult = async () => {
    const data = processData('put')
    if (!data)
      return

    await API.put(url, data)

    systemLayoutStore.update(state => {
      if (state.put)
        state.put = undefined
    })

    return true
  }

  const patchLayout: ApiPatchResult = async () => {
    const data = processData('patch')
    if (!data)
      return
    const patch = await API.patch(url, data)
    systemLayoutStore.update(patch.data.id, () => patch.data)

    return patch.data.id
  }

  const deleteLayout: ApiDeleteResult = async (id) => {
    const del = await API.delete(`${url}/${id}`)
    if (del) {
      systemLayoutStore.remove(id)
      return true
    }
  }

  const patchLayoutDefault: ApiPatchResult<ApiSystemLayoutResult> = async (layout) => {
    if (undefined === layout)
      throw Error("No layout provided to set as default")

    API.patch(`${url}/default`, {layout: layout.id}).then()
  }

  // Update store data when creating or editing a layout
  const setLayoutData = (key: ApiSystemLayoutKeys, value: ApiSystemLayoutTypes, method: "put" | "patch") => {
    systemLayoutStore.update(state => {
      let data: ApiSystemLayout = {}
      if (state[method])
        data = {...state[method]}

      if (Array.isArray(value))
        data[key] = [...value]
      else
        data[key] = value

      state[method] = data
    })

    setLayoutState(systemLayoutQuery.getValue()[method])
  }

  return {
    // Store data
    layoutState,
    setLayoutData,
    clearStoreState,

    // Api calls
    putLayout,
    patchLayout,
    patchLayoutDefault,
    deleteLayout,
  }
}

export default ApiSystemLayoutService
